'use client';

import { Typography } from '@mui/material';
import { ReactElement } from 'react';
import RequestLinkAction, { TRequestLinkActionProps } from './RequestLinkAction';
import { NO_OUTSTANDING_REQUESTS } from './requestCopy';

export type TRequestLinkSlotProps = Omit<TRequestLinkActionProps, 'url'> & {
  /**
   * The link a client sends their customer. Omit it when no face check task is open, which is both
   * the customer nobody ever asked and the customer who has already completed their check.
   */
  url?: string;
};

/**
 * The request link's own line on a check card, in both of the states it has.
 *
 * With a link it is `RequestLinkAction`. Without one it is the line `No outstanding requests`.
 *
 * **The caller passes what it has and does not branch.** Left to the view, the branch would put a
 * piece of product copy somewhere no test looks, and every call site would re-derive an
 * availability rule it can get wrong. That is the same reason the check cards resolve their own
 * guidance prose rather than taking it as a prop.
 *
 * **The evidence dialog does not use this, deliberately.** The line is card only. `EvidenceProvenance`
 * already states `Completed on`, `Requested on` and `Requested by` as three permanent fields
 * directly above where this line would sit in the dialog, so a vaguer restatement of them earns
 * nothing there. The dialog composes `RequestLinkAction` directly and renders nothing when there is
 * no link, and its footer anchors its button group to the right edge either way, so dropping the
 * link does not move the buttons.
 *
 * **Why `body2` and not the link's own size.** `RequestLinkAction`'s button is MUI `size="small"`,
 * which is 13px, and no type variant lands on 13px. A literal would violate ADR 78, and matching
 * the two optically is not observable anyway: they are mutually exclusive states, so no reader ever
 * sees them side by side.
 */
export default function RequestLinkSlot({
  url,
  ...requestLinkProps
}: Readonly<TRequestLinkSlotProps>): ReactElement {
  if (!url) {
    return (
      <Typography variant="body2" color="text.secondary">
        {NO_OUTSTANDING_REQUESTS}
      </Typography>
    );
  }

  return <RequestLinkAction url={url} {...requestLinkProps} />;
}
